import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Paper, Typography, TextField, Button, Grid, CircularProgress,
  Avatar, Chip, FormControl, InputLabel, Select, MenuItem, Divider,
  Dialog, DialogTitle, DialogContent, DialogActions, Alert, Snackbar,
  Autocomplete, Card, CardContent, IconButton, Tooltip, InputAdornment,
  Stepper, Step, StepLabel, Switch, FormControlLabel, Tabs, Tab,
} from "@mui/material";
import {
  Search as SearchIcon, PersonAdd as PersonAddIcon,
  Save as SaveIcon, Clear as ClearIcon, Verified as VerifiedIcon,
  Phone as PhoneIcon, Person as PersonIcon, LocationOn as LocationIcon,
  School as SchoolIcon, Work as WorkIcon, Favorite as FavoriteIcon,
  Assignment as AssignmentIcon, Star as StarIcon,
  CheckCircle as CheckCircleIcon, Info as InfoIcon,
  ArrowBack as ArrowBackIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { api_urls } from "../../../utils/api-urls";

const GENDERS = ["male", "female"];
const MARITAL_STATUSES = ["unmarried", "divorced", "widowed", "separated"];
const DIETS = ["vegetarian", "non-vegetarian", "vegan", "eggetarian"];
const RELIGIONS = ["Hindu", "Muslim", "Christian", "Sikh", "Buddhist", "Jain", "Other"];
const EDUCATION_OPTIONS = [
  "Below 10th", "10th Pass", "12th Pass", "Graduate", "Post Graduate",
  "Diploma", "PhD", "Professional Degree", "Other"
];
const INCOME_OPTIONS = [
  "Below 1 Lakh", "1-3 Lakh", "3-5 Lakh", "5-7 Lakh",
  "7-10 Lakh", "10-15 Lakh", "15-20 Lakh", "20-30 Lakh",
  "30-50 Lakh", "50 Lakh+", "1 Crore+"
];
const DISABILITY_OPTIONS = [
  { value: null, label: "None" },
  { value: "visual", label: "Visual Impairment" },
  { value: "hearing", label: "Hearing Impairment" },
  { value: "physical", label: "Physical Disability" },
  { value: "intellectual", label: "Intellectual Disability" },
  { value: "other", label: "Other" },
];

const INDIAN_STATES = [
  "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh",
  "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka",
  "Kerala", "Madhya Pradesh", "Maharashtra", "Manipur", "Meghalaya", "Mizoram",
  "Nagaland", "Odisha", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu",
  "Telangana", "Tripura", "Uttar Pradesh", "Uttarakhand", "West Bengal",
  "Delhi", "Jammu and Kashmir", "Ladakh", "Chandigarh", "Puducherry",
  "Andaman and Nicobar Islands", "Dadra and Nagar Haveli", "Lakshadweep"
];

const getProfileImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith("http")) return url;
  return "https://api.bharatdarshan.app" + url;
};

const AdminCreateProfile = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  // Form fields
  const [formData, setFormData] = useState({
    firstName: "", lastName: "", gender: "", age: "", aboutMe: "",
    disability: null,
    country: "India", state: "", city: "", pincode: "",
    religion: "", caste: "",
    maritalStatus: "", height: "", diet: "vegetarian", education: "",
    profession: "", company: "", income: "",
    motherDetail: "", fatherDetail: "",
    sistersCount: "", brothersCount: "",
    sampraday: "", guruJi: "", gotra: "",
    panNumber: "", aadhaarNumber: "",
    planId: "",
    isVerified: true, isDocumentVerified: false,
  });

  const steps = ["Select User", "Basic Info", "Location & Community", "Personal & Professional", "Documents & Plan"];

  // Fetch plans on mount
  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    setPlansLoading(true);
    try {
      const res = await fetch(`${api_urls}api/admins/plans`);
      const data = await res.json();
      if (data.success) {
        setPlans(data.data || []);
      }
    } catch (e) {
      console.error("Error fetching plans:", e);
    } finally {
      setPlansLoading(false);
    }
  };

  // Debounce search
  useEffect(() => {
    if (searchQuery.length < 2) {
      setSearchResults([]);
      return;
    }
    const timer = setTimeout(() => searchUsers(searchQuery), 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const searchUsers = async (query) => {
    setSearchLoading(true);
    try {
      const res = await fetch(`${api_urls}api/admins/search-all-users?search=${encodeURIComponent(query)}&limit=30`);
      const data = await res.json();
      if (data.success) {
        setSearchResults(data.data || []);
      }
    } catch (e) {
      console.error("Search error:", e);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSelectUser = (user) => {
    setSelectedUser(user);
    // Auto-fill form with existing user data
    setFormData({
      firstName: user.firstName || "",
      lastName: user.lastName || "",
      gender: user.gender || "",
      age: user.age ? String(user.age) : "",
      aboutMe: user.aboutMe || user.bio || "",
      disability: user.disability || null,
      country: user.country || "India",
      state: user.state || "",
      city: user.city || "",
      pincode: user.pincode || "",
      religion: user.religion || "",
      caste: user.caste || "",
      maritalStatus: user.maritalStatus || "",
      height: user.height ? String(user.height) : "",
      diet: user.diet || "vegetarian",
      education: user.education || "",
      profession: user.profession || "",
      company: user.company || "",
      income: user.income || "",
      motherDetail: user.motherDetail || "",
      fatherDetail: user.fatherDetail || "",
      sistersCount: user.sistersCount != null ? String(user.sistersCount) : "",
      brothersCount: user.brothersCount != null ? String(user.brothersCount) : "",
      sampraday: user.sampraday || "",
      guruJi: user.guruJi || "",
      gotra: user.gotra || "",
      panNumber: "",
      aadhaarNumber: "",
      planId: user.membershipPlan?._id || user.membershipPlan || "",
      isVerified: user.isVerified || true,
      isDocumentVerified: user.isDocumentVerified || false,
    });
    setActiveStep(1);
    setSnackbar({ open: true, message: `User "${user.firstName || user.name || user.phone}" selected. Details auto-filled.`, severity: "info" });
  };

  const handleChange = (field) => (e) => {
    setFormData(prev => ({
      ...prev,
      [field]: e.target.value,
    }));
  };

  const handleSelectChange = (field) => (e) => {
    setFormData(prev => ({
      ...prev,
      [field]: e.target.value,
    }));
  };

  const handleSwitchChange = (field) => (e) => {
    setFormData(prev => ({
      ...prev,
      [field]: e.target.checked,
    }));
  };

  const handleSubmit = async () => {
    if (!selectedUser) {
      setSnackbar({ open: true, message: "Please select a user first!", severity: "error" });
      return;
    }

    if (!formData.firstName || !formData.gender) {
      setSnackbar({ open: true, message: "First Name and Gender are required!", severity: "error" });
      return;
    }

    setSaving(true);
    try {
      const payload = {
        userId: selectedUser._id,
        ...formData,
        age: formData.age ? Number(formData.age) : undefined,
        height: formData.height ? Number(formData.height) : undefined,
        sistersCount: formData.sistersCount ? Number(formData.sistersCount) : undefined,
        brothersCount: formData.brothersCount ? Number(formData.brothersCount) : undefined,
      };

      // Remove empty strings
      Object.keys(payload).forEach(key => {
        if (payload[key] === "" || payload[key] === undefined) {
          delete payload[key];
        }
      });

      const res = await fetch(`${api_urls}api/admins/create-profile`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      
      if (data.success) {
        setSnackbar({ open: true, message: "Milan Sathi profile created/updated successfully! ✅", severity: "success" });
        // Reset after 2 seconds
        setTimeout(() => {
          navigate("/milan-sathi/users");
        }, 2000);
      } else {
        setSnackbar({ open: true, message: data.message || "Failed to create profile", severity: "error" });
      }
    } catch (e) {
      console.error("Submit error:", e);
      setSnackbar({ open: true, message: "Network error. Please try again.", severity: "error" });
    } finally {
      setSaving(false);
    }
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return renderUserSearch();
      case 1:
        return renderBasicInfo();
      case 2:
        return renderLocationCommunity();
      case 3:
        return renderPersonalProfessional();
      case 4:
        return renderDocumentsPlan();
      default:
        return null;
    }
  };

  // ===== Step 0: User Search =====
  const renderUserSearch = () => (
    <Box>
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
        🔍 Search Existing User
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Search by name, phone number, or email. Select a user to auto-fill their details.
      </Typography>

      <TextField
        fullWidth
        label="Search by Name / Phone / Email"
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        placeholder="Enter at least 2 characters..."
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
          endAdornment: searchLoading ? <CircularProgress size={20} /> : null,
        }}
        sx={{ mb: 3 }}
      />

      {/* Search Results */}
      {searchResults.length > 0 && (
        <Box sx={{ maxHeight: 400, overflowY: "auto" }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Found {searchResults.length} users:
          </Typography>
          {searchResults.map((user) => (
            <Card
              key={user._id}
              sx={{
                mb: 1, cursor: "pointer",
                border: selectedUser?._id === user._id ? "2px solid #4CAF50" : "1px solid #e0e0e0",
                "&:hover": { bgcolor: "#f5f5f5", borderColor: "#2196F3" },
                transition: "all 0.2s",
              }}
              onClick={() => handleSelectUser(user)}
            >
              <CardContent sx={{ display: "flex", alignItems: "center", p: 1.5, "&:last-child": { pb: 1.5 } }}>
                <Avatar
                  src={getProfileImageUrl(user.profileImageUrl)}
                  sx={{ width: 45, height: 45, mr: 2, bgcolor: user.gender === "female" ? "#E91E63" : "#2196F3" }}
                >
                  {(user.firstName || user.name || "?")[0]?.toUpperCase()}
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    {user.firstName || ""} {user.lastName || ""} {!user.firstName && !user.lastName ? user.name || "N/A" : ""}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    📱 {user.phone || user.mobile || "N/A"} &nbsp;|&nbsp;
                    📧 {user.email || "N/A"} &nbsp;|&nbsp;
                    📍 {user.city || "N/A"}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                  {user.isMilanSathiProfile === "true" && (
                    <Chip label="Milan Sathi" size="small" color="primary" variant="outlined" />
                  )}
                  {user.isPremium && (
                    <Chip label="Premium" size="small" color="warning" icon={<StarIcon />} />
                  )}
                  {user.isVerified && (
                    <Chip label="Verified" size="small" color="success" icon={<VerifiedIcon />} />
                  )}
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      {searchQuery.length >= 2 && searchResults.length === 0 && !searchLoading && (
        <Alert severity="info" sx={{ mt: 2 }}>
          No users found matching "{searchQuery}". User must first register in the Bharat Darshan app.
        </Alert>
      )}
    </Box>
  );

  // ===== Step 1: Basic Info =====
  const renderBasicInfo = () => (
    <Box>
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
        👤 Basic Information
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField fullWidth required label="First Name" value={formData.firstName} onChange={handleChange("firstName")} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField fullWidth label="Last Name" value={formData.lastName} onChange={handleChange("lastName")} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth required>
            <InputLabel>Gender</InputLabel>
            <Select value={formData.gender} label="Gender" onChange={handleSelectChange("gender")}>
              {GENDERS.map(g => <MenuItem key={g} value={g}>{g.charAt(0).toUpperCase() + g.slice(1)}</MenuItem>)}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField fullWidth label="Age" type="number" value={formData.age} onChange={handleChange("age")}
            inputProps={{ min: 18, max: 80 }} />
        </Grid>
        <Grid item xs={12}>
          <TextField fullWidth multiline rows={3} label="About Me / Bio" value={formData.aboutMe} onChange={handleChange("aboutMe")}
            placeholder="Write about the user..." />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel>Disability</InputLabel>
            <Select value={formData.disability || ""} label="Disability" onChange={(e) => setFormData(prev => ({ ...prev, disability: e.target.value || null }))}>
              {DISABILITY_OPTIONS.map(d => <MenuItem key={d.label} value={d.value || ""}>{d.label}</MenuItem>)}
            </Select>
          </FormControl>
        </Grid>
      </Grid>
    </Box>
  );

  // ===== Step 2: Location & Community =====
  const renderLocationCommunity = () => (
    <Box>
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
        📍 Location & Community
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField fullWidth label="Country" value={formData.country} onChange={handleChange("country")} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel>State</InputLabel>
            <Select value={formData.state} label="State" onChange={handleSelectChange("state")}>
              {INDIAN_STATES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField fullWidth label="City" value={formData.city} onChange={handleChange("city")} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField fullWidth label="Pincode" value={formData.pincode} onChange={handleChange("pincode")} />
        </Grid>

        <Grid item xs={12}><Divider sx={{ my: 1 }}><Chip label="Community Details" size="small" /></Divider></Grid>

        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel>Religion</InputLabel>
            <Select value={formData.religion} label="Religion" onChange={handleSelectChange("religion")}>
              {RELIGIONS.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField fullWidth label="Caste" value={formData.caste} onChange={handleChange("caste")} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField fullWidth label="Sampraday" value={formData.sampraday} onChange={handleChange("sampraday")} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField fullWidth label="Guru Ji" value={formData.guruJi} onChange={handleChange("guruJi")} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField fullWidth label="Gotra" value={formData.gotra} onChange={handleChange("gotra")} />
        </Grid>
      </Grid>
    </Box>
  );

  // ===== Step 3: Personal & Professional =====
  const renderPersonalProfessional = () => (
    <Box>
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
        💼 Personal & Professional Details
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel>Marital Status</InputLabel>
            <Select value={formData.maritalStatus} label="Marital Status" onChange={handleSelectChange("maritalStatus")}>
              {MARITAL_STATUSES.map(s => <MenuItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</MenuItem>)}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={3}>
          <TextField fullWidth label="Height (cm)" type="number" value={formData.height} onChange={handleChange("height")}
            inputProps={{ min: 100, max: 250 }} />
        </Grid>
        <Grid item xs={12} sm={3}>
          <FormControl fullWidth>
            <InputLabel>Diet</InputLabel>
            <Select value={formData.diet} label="Diet" onChange={handleSelectChange("diet")}>
              {DIETS.map(d => <MenuItem key={d} value={d}>{d.charAt(0).toUpperCase() + d.slice(1)}</MenuItem>)}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel>Education</InputLabel>
            <Select value={formData.education} label="Education" onChange={handleSelectChange("education")}>
              {EDUCATION_OPTIONS.map(e => <MenuItem key={e} value={e}>{e}</MenuItem>)}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField fullWidth label="Profession" value={formData.profession} onChange={handleChange("profession")} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField fullWidth label="Company" value={formData.company} onChange={handleChange("company")} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel>Annual Income</InputLabel>
            <Select value={formData.income} label="Annual Income" onChange={handleSelectChange("income")}>
              {INCOME_OPTIONS.map(i => <MenuItem key={i} value={i}>{i}</MenuItem>)}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12}><Divider sx={{ my: 1 }}><Chip label="Family Details" size="small" /></Divider></Grid>

        <Grid item xs={12} sm={6}>
          <TextField fullWidth label="Father's Occupation" value={formData.fatherDetail} onChange={handleChange("fatherDetail")}
            placeholder="e.g., Retired Government Officer" />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField fullWidth label="Mother's Occupation" value={formData.motherDetail} onChange={handleChange("motherDetail")}
            placeholder="e.g., Homemaker" />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField fullWidth label="Number of Brothers" type="number" value={formData.brothersCount} onChange={handleChange("brothersCount")}
            inputProps={{ min: 0, max: 10 }} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField fullWidth label="Number of Sisters" type="number" value={formData.sistersCount} onChange={handleChange("sistersCount")}
            inputProps={{ min: 0, max: 10 }} />
        </Grid>
      </Grid>
    </Box>
  );

  // ===== Step 4: Documents & Plan =====
  const renderDocumentsPlan = () => (
    <Box>
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
        📄 Documents & Plan Assignment
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12}><Divider sx={{ my: 0 }}><Chip label="Identity Documents" size="small" /></Divider></Grid>

        <Grid item xs={12} sm={6}>
          <TextField fullWidth label="PAN Number" value={formData.panNumber} onChange={handleChange("panNumber")}
            placeholder="e.g., ABCDE1234F"
            inputProps={{ maxLength: 10, style: { textTransform: "uppercase" } }} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField fullWidth label="Aadhaar Number" value={formData.aadhaarNumber} onChange={handleChange("aadhaarNumber")}
            placeholder="e.g., 1234 5678 9012"
            inputProps={{ maxLength: 14 }} />
        </Grid>

        <Grid item xs={12}><Divider sx={{ my: 1 }}><Chip label="Plan Assignment" size="small" color="primary" /></Divider></Grid>

        <Grid item xs={12}>
          <FormControl fullWidth>
            <InputLabel>Assign Plan</InputLabel>
            <Select value={formData.planId} label="Assign Plan" onChange={handleSelectChange("planId")}>
              <MenuItem value="">No Plan (Free)</MenuItem>
              {plans.map(plan => (
                <MenuItem key={plan._id} value={plan._id}>
                  {plan.name} - ₹{plan.offerPrice || plan.price} / {plan.duration} ({plan.connectionLimit} connections)
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {formData.planId && (
            <Alert severity="info" sx={{ mt: 1 }}>
              User will be marked as Premium and plan will be activated immediately.
              Expiry will be calculated from today.
            </Alert>
          )}
        </Grid>

        <Grid item xs={12}><Divider sx={{ my: 1 }}><Chip label="Verification Status" size="small" color="success" /></Divider></Grid>

        <Grid item xs={12} sm={6}>
          <FormControlLabel
            control={<Switch checked={formData.isVerified} onChange={handleSwitchChange("isVerified")} color="success" />}
            label="Profile Verified ✅"
          />
          <Typography variant="caption" color="text.secondary" display="block">
            Admin-verified profiles are shown with a verified badge
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControlLabel
            control={<Switch checked={formData.isDocumentVerified} onChange={handleSwitchChange("isDocumentVerified")} color="success" />}
            label="Documents Verified 📄"
          />
          <Typography variant="caption" color="text.secondary" display="block">
            Mark if Aadhaar/PAN documents have been verified
          </Typography>
        </Grid>
      </Grid>
    </Box>
  );

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: "auto" }}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", mb: 3, gap: 2 }}>
        <IconButton onClick={() => navigate("/milan-sathi/users")} sx={{ bgcolor: "#f5f5f5" }}>
          <ArrowBackIcon />
        </IconButton>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: "#E91E63" }}>
            <PersonAddIcon sx={{ mr: 1, verticalAlign: "middle" }} />
            Create Milan Sathi Profile
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Create or update a complete Milan Sathi profile for any existing user
          </Typography>
        </Box>
      </Box>

      {/* Selected User Info Card */}
      {selectedUser && (
        <Card sx={{ mb: 3, bgcolor: "#E8F5E9", border: "1px solid #4CAF50" }}>
          <CardContent sx={{ display: "flex", alignItems: "center", p: 2, "&:last-child": { pb: 2 } }}>
            <Avatar
              src={getProfileImageUrl(selectedUser.profileImageUrl)}
              sx={{ width: 55, height: 55, mr: 2, border: "2px solid #4CAF50" }}
            >
              {(selectedUser.firstName || selectedUser.name || "?")[0]?.toUpperCase()}
            </Avatar>
            <Box>
              <Typography variant="subtitle1" fontWeight={600}>
                {selectedUser.firstName || ""} {selectedUser.lastName || ""} {!selectedUser.firstName ? selectedUser.name || "" : ""}
              </Typography>
              <Typography variant="body2">
                📱 {selectedUser.phone || selectedUser.mobile || "N/A"} &nbsp;|&nbsp;
                📧 {selectedUser.email || "N/A"} &nbsp;|&nbsp;
                📍 {selectedUser.city || "N/A"}, {selectedUser.state || ""}
              </Typography>
              <Box sx={{ display: "flex", gap: 0.5, mt: 0.5 }}>
                {selectedUser.isMilanSathiProfile === "true" && <Chip label="Existing Milan Sathi" size="small" color="warning" />}
                {selectedUser.isMilanSathiProfile !== "true" && <Chip label="New Milan Sathi Profile" size="small" color="success" />}
              </Box>
            </Box>
            <Box sx={{ ml: "auto" }}>
              <Button variant="outlined" color="error" size="small" onClick={() => { setSelectedUser(null); setActiveStep(0); }}>
                Change User
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Stepper */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3 }}>
          {steps.map((label, index) => (
            <Step key={label} completed={activeStep > index}>
              <StepLabel
                sx={{ cursor: selectedUser || index === 0 ? "pointer" : "default" }}
                onClick={() => {
                  if (index === 0 || selectedUser) setActiveStep(index);
                }}
              >
                {label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>

        <Divider sx={{ mb: 3 }} />

        {/* Step Content */}
        {renderStepContent()}

        {/* Navigation Buttons */}
        <Box sx={{ display: "flex", justifyContent: "space-between", mt: 4 }}>
          <Button
            variant="outlined"
            disabled={activeStep === 0}
            onClick={() => setActiveStep(prev => prev - 1)}
          >
            Back
          </Button>

          <Box sx={{ display: "flex", gap: 2 }}>
            {activeStep < steps.length - 1 ? (
              <Button
                variant="contained"
                disabled={activeStep === 0 && !selectedUser}
                onClick={() => setActiveStep(prev => prev + 1)}
                sx={{ bgcolor: "#E91E63", "&:hover": { bgcolor: "#C2185B" } }}
              >
                Next
              </Button>
            ) : (
              <Button
                variant="contained"
                color="success"
                size="large"
                startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                disabled={saving || !selectedUser}
                onClick={handleSubmit}
                sx={{ minWidth: 200, fontSize: 16, fontWeight: 700 }}
              >
                {saving ? "Saving..." : "Create / Update Profile"}
              </Button>
            )}
          </Box>
        </Box>
      </Paper>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity={snackbar.severity} variant="filled" onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminCreateProfile;
